import TeamMenu from 'components/app-components/contractors/TeamMenu';
import Button from 'components/app-components/ui/button';
import { useCompany } from 'context/company';
import { useUser } from 'context/user';
import { useRouteAuthorization } from 'hooks/useRouteAuthorization';
import { useTeamsSWR } from 'hooks/useTeamsSWR';
import CloseIcon from 'icons/Close';
import LeftArrow from 'icons/LeftArrow';
import RightArrow from 'icons/RightArrow';
import theraToast from 'components/generic-components/toast/TheraToast';
import { PinIcon, XIcon } from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { getAvailableTeamsToSwitch } from 'utils/affiliations';
import { useNavigation } from './NavigationContext';
import { SubNavItem } from './SubNavItem';
import type { SubNavItem as SubNavItemType } from './types';
import { isTabShown } from './navResolution';
import { ShortcutsModal } from './shortcuts/ShortcutsModal';
import { useNavShortcuts } from './shortcuts/useNavShortcuts';

function Sidebar() {
  const router = useRouter();
  const [subNavsExpanded, setSubMenuExpanded] = useState<{ [subNavName: string]: boolean }>({});
  const [isShortcutsModalOpen, setIsShortcutsModalOpen] = useState(false);
  const { groups: shortcutGroups, shortcuts, saveShortcuts, isSaving, maxSelectable } = useNavShortcuts();

  const removeShortcut = async (href: string) => {
    try {
      await saveShortcuts(shortcuts.map((shortcut) => shortcut.href).filter((path) => path !== href));
    } catch {
      theraToast.error({ message: 'Could not remove this shortcut. Please try again.' });
    }
  };
  const {
    navigation,
    activeMainNav,
    activeSubNav,
    isMobileMenuOpen,
    hasSidebar,
    toggleMobileMenu,
    isSidebarCollapsed,
    toggleSidebarCollapsed,
    hasBanner,
  } = useNavigation();

  useEffect(() => {
    if (isSidebarCollapsed) {
      setSubMenuExpanded({});
    }
  }, [isSidebarCollapsed]);

  const {
    affiliationsByCompany,
    currentTeamId,
    setCurrentTeamId,
    isLoading: isUserLoading,
    roleType,
  } = useUser() || {};
  const { company, isLoading: isCompanyLoading } = useCompany() || {};
  const { teams, isPending: isTeamsLoading } = useTeamsSWR();
  const { isAllowed } = useRouteAuthorization();
  const isUSCompany = company?.address?.country === 'US';
  // Check for callerUserBeneficiaryInformation to ensure beneficiary onboarding is complete
  const isBankingDetailsSubmitted =
    isUSCompany && Boolean(company?.companyBankingDetails?.callerUserBeneficiaryInformation);
  const isManager = roleType === 'manager';
  const isIncorporationCompleted =
    isUSCompany && Boolean(company?.incorporationDetails) ? company?.incorporationDetails?.status === 'complete' : true;

  const isOnboardingPending =
    isUSCompany && !isBankingDetailsSubmitted && company?.settings?.isTpOnboardingSkipped && isIncorporationCompleted;

  const companyTeams = affiliationsByCompany?.[company?.companyID]?.teams;
  const availableTeamsToSwitch = useMemo(() => getAvailableTeamsToSwitch(teams, companyTeams), [companyTeams, teams]);
  const canShowTeamMenu = teams && Object.keys(teams)?.length > 0;
  const isLoading = isUserLoading || isCompanyLoading || isTeamsLoading;

  // Memoized so its identity only changes when the underlying nav does - the effect below is keyed
  // on this array, and a fresh reference on every render would fire it on every render too, not just
  // when the active section actually changes.
  const subNavsToBeShown = useMemo(
    () => navigation[activeMainNav]?.subNavs?.filter((subNav) => subNav.isShownInBar && subNav.isDisabled !== true),
    [navigation, activeMainNav],
  );

  // `activeSubNav` only catches up once the route change actually completes (Navbar.tsx resolves it
  // from router.pathname), while a click can clear or toggle subNavsExpanded immediately. Writing the
  // auto-expand during render (keyed off the same lagging activeSubNav) used to race that click: it
  // either undid the clear before it painted, or toggled a stale value into a real collapse that then
  // snapped back. Doing it in its own effect means it only runs once activeSubNav truly changes.
  useEffect(() => {
    if (isSidebarCollapsed) return;
    const activeItem = subNavsToBeShown?.[activeSubNav];
    const hasVisibleTabs = activeItem?.subNavs?.some(isTabShown);
    setSubMenuExpanded(activeItem && hasVisibleTabs ? { [activeItem.name]: true } : {});
  }, [activeSubNav, subNavsToBeShown, isSidebarCollapsed]);

  const checkIfSubNavExpanded = useCallback(
    (item: SubNavItemType, index: number) => {
      // Expand whenever this section is the active one and it has visible tabs to reveal. Keying off
      // the section (not off a specific active tab) keeps the tabs visible on section pages that have
      // no dedicated tab of their own (e.g. Compliance, Run Payroll).
      const hasVisibleTabs = item?.subNavs?.some(isTabShown);
      return Boolean(hasVisibleTabs) && activeSubNav !== -1 && index === activeSubNav;
    },
    [activeSubNav],
  );

  const setSubNavExpanded = (name: string) => {
    if (!name) {
      setSubMenuExpanded({});
      return;
    }
    const updatedExpandedState = { [name]: !subNavsExpanded[name] };
    setSubMenuExpanded(updatedExpandedState);
  };

  if (!hasSidebar) {
    return null;
  }

  if (isLoading) {
    return <SidebarSkeleton isCollapsed={isSidebarCollapsed} />;
  }

  return (
    <>
      <div
        onClick={toggleMobileMenu}
        className={`fixed inset-0 z-10 bg-black/50 transition-opacity lg:hidden ${
          isMobileMenuOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        aria-hidden="true"
      />
      <div
        id="subNav-wrapper"
        className={`fixed top-0 mt-0 flex w-[280px] flex-col border-l border-neutral-200 bg-white/70 px-3 backdrop-blur-xl transition-all duration-300 ease-in-out lg:bg-white lg:backdrop-blur-none ${
          hasBanner ? 'lg:mt-[100px]' : 'lg:mt-[65px]'
        } ${
          isMobileMenuOpen ? 'z-[100] translate-x-0' : 'translate-x-full'
        } bottom-0 right-0 pt-[calc(50px+var(--safe-top))] lg:left-0 lg:right-auto lg:pt-4 ${
          isSidebarCollapsed
            ? 'lg:w-[80px] lg:min-w-[80px] lg:max-w-[80px]'
            : 'lg:w-[244px] lg:min-w-[244px] lg:max-w-[244px]'
        } lg:translate-x-0 lg:border-l-0 lg:border-r`}
      >
        {isMobileMenuOpen && (
          <div className="absolute right-4 top-[calc(1.25rem+var(--safe-top))]">
            <Button variant="plain" onClick={toggleMobileMenu}>
              <CloseIcon className="h-6 w-6 text-neutral-700" />
              <span className="sr-only">Close menu</span>
            </Button>
          </div>
        )}
        {subNavsToBeShown?.some((item) => item.isCustomElement && item.name === 'teamMenu') && canShowTeamMenu && (
          <TeamMenu
            selectedTeamId={currentTeamId}
            setSelectedTeamId={setCurrentTeamId}
            teams={availableTeamsToSwitch}
            key="teamMenu"
            isSidebarCollapsed={isSidebarCollapsed && !isMobileMenuOpen}
          />
        )}
        {(() => {
          const isCollapsed = isSidebarCollapsed && !isMobileMenuOpen;
          return (
            <div className="hide-scrollbar -mx-3 min-h-0 flex-1 overflow-y-auto px-3">
              <ul
                className={`my-2 flex max-w-2xl flex-col items-start gap-1 pb-4 ${
                  isOnboardingPending && isManager ? 'lg:my-0' : 'lg:my-0'
                } lg:w-full`}
              >
                {subNavsToBeShown?.map((item, index) => {
                  if (item.isCustomElement && item.name === 'teamMenu') {
                    return null;
                  }
                  if (!isAllowed(item?.onlyShownTo)) {
                    return null;
                  }
                  const isDefaultExpanded = checkIfSubNavExpanded(item, index);
                  const isManualExpanded = subNavsExpanded?.[item.name] === true;
                  const isExpanded =
                    (isDefaultExpanded || isManualExpanded || (item?.category ? true : false)) &&
                    (!isCollapsed || Boolean(item?.category));
                  return (
                    <SubNavItem
                      key={item.name}
                      item={item}
                      index={index}
                      isExpanded={isExpanded}
                      setSubNavExpanded={setSubNavExpanded}
                    />
                  );
                })}
              </ul>
              {shortcutGroups.length > 0 && !isCollapsed && (
                <div className="flex flex-col gap-1 pb-4">
                  {!isCollapsed && (
                    <div className="flex items-center justify-between px-2.5 py-1.5">
                      <span className="text-sm font-medium text-neutral-500">Shortcuts</span>
                      <button
                        type="button"
                        onClick={() => {
                          if (isMobileMenuOpen) toggleMobileMenu();
                          setIsShortcutsModalOpen(true);
                        }}
                        className="text-sm font-medium text-neutral-800 underline hover:opacity-70"
                      >
                        {shortcuts.length > 0 ? 'Edit' : 'Add'}
                      </button>
                    </div>
                  )}
                  <ul className="flex flex-col gap-1">
                    {shortcuts.map((shortcut) => {
                      const isActive = router.asPath.split('?')[0] === shortcut.href;
                      return (
                        <li key={shortcut.href} className="group relative">
                          <Link
                            href={shortcut.href}
                            title={isCollapsed ? shortcut.label : undefined}
                            onClick={toggleMobileMenu}
                            className={`flex items-center rounded-md py-1.5 hover:bg-neutral-100 ${
                              isCollapsed ? 'justify-center' : 'pl-2.5 pr-9'
                            } ${isActive ? 'bg-neutral-100' : ''}`}
                          >
                            <span className="flex h-6 w-6 items-center justify-center text-neutral-500">
                              <PinIcon className="h-4 w-4" />
                            </span>
                            {!isCollapsed && (
                              <span
                                className={`ml-3 truncate text-sm ${isActive ? 'text-therablue dark:text-white' : 'text-neutral-600'}`}
                              >
                                {shortcut.label}
                              </span>
                            )}
                          </Link>
                          {!isCollapsed && (
                            <button
                              type="button"
                              aria-label={`Remove ${shortcut.label} from shortcuts`}
                              disabled={isSaving}
                              onClick={() => removeShortcut(shortcut.href)}
                              className="absolute right-1.5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md text-neutral-500 opacity-0 transition-opacity group-hover:opacity-100 hover:bg-neutral-200 hover:text-neutral-900 focus-visible:opacity-100 disabled:cursor-not-allowed"
                            >
                              <XIcon className="h-4 w-4" />
                            </button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          );
        })()}
        <div
          className={`mt-auto hidden border-t border-neutral-200 py-3 lg:flex ${
            isSidebarCollapsed ? 'justify-center' : 'justify-end pr-2'
          }`}
        >
          <button
            onClick={toggleSidebarCollapsed}
            className="flex h-8 w-8 items-center justify-center rounded-md text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900"
          >
            {isSidebarCollapsed ? <RightArrow className="h-4 w-4" /> : <LeftArrow className="h-4 w-4" />}
          </button>
        </div>
      </div>
      <ShortcutsModal
        isOpen={isShortcutsModalOpen}
        onClose={() => setIsShortcutsModalOpen(false)}
        groups={shortcutGroups}
        selected={shortcuts}
        maxSelectable={maxSelectable}
        isSaving={isSaving}
        onSave={saveShortcuts}
      />
    </>
  );
}

export default memo(Sidebar);

export const SidebarSkeleton = ({ isCollapsed = false }: { isCollapsed?: boolean }) => (
  <div
    id="subNav-wrapper-skeleton"
    className={`fixed left-0 top-0 hidden min-h-[100vh] border-r border-neutral-200 bg-white px-3 pt-4 md:block ${
      isCollapsed ? 'w-[80px] max-w-[80px]' : 'w-[244px] max-w-[244px]'
    }`}
  >
    <div className="mt-2 flex animate-pulse flex-col gap-y-6 md:mt-16">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className={`flex w-full items-center ${isCollapsed ? 'justify-center' : 'space-x-4'}`}>
          <div className="h-6 w-6 rounded-md bg-neutral-200"></div>
          {!isCollapsed && <div className="h-4 w-2/3 rounded-md bg-neutral-200"></div>}
        </div>
      ))}
    </div>
  </div>
);
